// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2025-2026 Daragh Farrell

/**
 * Error reporting utility for ClubView.
 *
 * When VITE_SENTRY_DSN is set, reports go to Sentry (including native crashes
 * via @sentry/capacitor). Otherwise falls back to console logging.
 */

import * as Sentry from '@sentry/capacitor';

type ErrorContext = Record<string, unknown>;

let currentUserId: string | null = null;
let sentryEnabled = false;
let initialised = false;

/** Report an error with optional context metadata. */
export function reportError(error: unknown, context?: ErrorContext): void {
  if (sentryEnabled) {
    const extra = { ...context, url: typeof window !== 'undefined' ? window.location.href : undefined };
    if (error instanceof Error) {
      Sentry.captureException(error, { extra });
    } else {
      Sentry.captureMessage(String(error), { extra });
    }
  } else {
    // Fallback: console logging when Sentry is not configured
    const payload = {
      error: error instanceof Error ? { message: error.message, stack: error.stack } : String(error),
      userId: currentUserId,
      context,
      timestamp: new Date().toISOString(),
      url: typeof window !== 'undefined' ? window.location.href : undefined,
    };
    console.error('[ClubView Error]', payload);
  }
}

/** Associate subsequent error reports with a member ID and club. No PII is sent. */
export function setUser(memberId: string | null, clubId?: string): void {
  currentUserId = memberId;

  if (sentryEnabled) {
    Sentry.setUser(memberId ? { id: memberId } : null);
    if (clubId) {
      Sentry.setTag('club_id', clubId);
    }
  }
}

/** Get the currently set user ID (useful for testing). */
export function getUser(): string | null {
  return currentUserId;
}

/** Check if error reporting has been initialised. */
export function isInitialised(): boolean {
  return initialised;
}

/** Initialise error reporting. Call once at app startup. */
export function initErrorReporting(): void {
  if (initialised) return;
  if (typeof window === 'undefined') return;

  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const release = import.meta.env.VITE_APP_VERSION || '0.0.0';
  const environment = import.meta.env.MODE || 'production';

  if (dsn) {
    Sentry.init({
      dsn,
      release,
      environment,
      tracesSampleRate: environment === 'production' ? 0.2 : 1.0,
      beforeSend(event) {
        // Strip PII — only member ID and club ID are allowed
        if (event.user) {
          event.user = { id: event.user.id };
        }
        return event;
      },
    });
    sentryEnabled = true;
  }

  // Register global error handlers regardless of Sentry
  window.addEventListener('error', (event: ErrorEvent) => {
    reportError(event.error ?? event.message, {
      source: 'window.onerror',
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  });

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    reportError(event.reason, { source: 'unhandledrejection' });
  });

  initialised = true;
}
